import { useMemo, useState } from 'react';
import { Loader2, RotateCcw, Search, X } from 'lucide-react';
import { FaTrashCan } from '../ui/FaIcons';
import { toast } from 'sonner';
import {
  Dialog,
  DialogContent,
  DialogTitle,
  DialogDescription,
} from '../ui/dialog';
import { Input } from '../ui/Input';
import {
  useArchivedTasks,
  useRestoreTask,
  type Task,
  type TaskSection,
} from '../../hooks/queries/useTasks';
import { getPriorityMeta, getDueDate, formatDueDate } from './taskHelpers';

import { formatAppDate } from '../../lib/dateFormat';
interface ArchivedTasksModalProps {
  open: boolean;
  onClose: () => void;
  dossierId: string | null;
  sections: TaskSection[];
}

export function ArchivedTasksModal({
  open,
  onClose,
  dossierId,
  sections,
}: ArchivedTasksModalProps) {
  const [search, setSearch] = useState('');
  const filters = useMemo(
    () => ({
      dossierId,
      search: search.trim() || undefined,
    }),
    [dossierId, search],
  );
  const { data: tasks = [], isLoading } = useArchivedTasks(filters);
  const restoreMut = useRestoreTask();

  const sectionsById = useMemo(() => {
    const m = new Map<string, TaskSection>();
    for (const s of sections) m.set(s.id, s);
    return m;
  }, [sections]);

  function handleRestore(task: Task) {
    restoreMut.mutate(task.id, {
      onSuccess: () => toast.success(`« ${task.content} » restaurée.`),
      onError: (err) => toast.error(`Restauration impossible : ${err.message}`),
    });
  }

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-3xl bg-white p-0 gap-0 [&>button]:hidden">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-neutral-200">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-cime-primary/10 flex items-center justify-center">
              <FaTrashCan className="w-5 h-5 text-cime-primary" />
            </div>
            <div>
              <DialogTitle
                className="text-lg font-semibold text-cime-text-primary"
                style={{ fontFamily: 'var(--cime-font-heading)' }}
              >
                Corbeille
              </DialogTitle>
              <DialogDescription className="text-xs text-cime-text-muted">
                {tasks.length} tâche{tasks.length > 1 ? 's' : ''} supprimée
                {tasks.length > 1 ? 's' : ''} • restaurez à tout moment
              </DialogDescription>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="text-neutral-400 hover:text-cime-text-primary p-1"
            aria-label="Fermer"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Filtres */}
        <div className="px-6 py-3 border-b border-neutral-100 bg-neutral-50/40">
          <div className="relative">
            <Search className="w-4 h-4 absolute left-2.5 top-1/2 -translate-y-1/2 text-neutral-400 pointer-events-none" />
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Rechercher dans le titre…"
              className="pl-8"
            />
          </div>
        </div>

        {/* Liste */}
        <div className="max-h-[60vh] overflow-y-auto">
          {isLoading && (
            <div className="flex items-center justify-center py-12 text-cime-text-muted">
              <Loader2 className="w-5 h-5 animate-spin mr-2" />
              Chargement…
            </div>
          )}

          {!isLoading && tasks.length === 0 && (
            <div className="flex flex-col items-center justify-center py-12 text-center">
              <FaTrashCan className="w-8 h-8 text-neutral-300 mb-2" />
              <p className="text-sm text-cime-text-muted">
                {search
                  ? 'Aucune tâche supprimée ne correspond à votre recherche.'
                  : 'La corbeille est vide.'}
              </p>
            </div>
          )}

          {!isLoading && tasks.length > 0 && (
            <ul className="divide-y divide-neutral-100">
              {tasks.map((task) => {
                const meta = getPriorityMeta(task.priority);
                const due = getDueDate(task);
                const section = task.section_id
                  ? sectionsById.get(task.section_id)
                  : null;
                const archivedAt = task.deleted_at
                  ? formatAppDate(task.deleted_at)
                  : '—';
                return (
                  <li
                    key={task.id}
                    className="flex items-center gap-3 px-6 py-3 hover:bg-neutral-50 group"
                  >
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-medium text-cime-text-primary truncate">
                        {task.content}
                      </div>
                      <div className="flex items-center gap-2 mt-0.5 text-[11px] text-cime-text-muted">
                        {section && (
                          <span className="inline-flex items-center gap-1">
                            <span
                              className="w-1.5 h-1.5 rounded-full"
                              style={{ backgroundColor: section.color ?? '#94a3b8' }}
                              aria-hidden
                            />
                            {section.name}
                          </span>
                        )}
                        {due && <span className="tabular-nums">{formatDueDate(due)}</span>}
                        <span className="tabular-nums">supprimée le {archivedAt}</span>
                      </div>
                    </div>

                    <span
                      className={`text-xs font-medium px-1.5 py-0.5 rounded border ${meta.className}`}
                    >
                      {meta.label}
                    </span>

                    <button
                      type="button"
                      onClick={() => handleRestore(task)}
                      disabled={restoreMut.isPending}
                      className="opacity-0 group-hover:opacity-100 inline-flex items-center gap-1 px-2 py-1 rounded-md text-xs font-medium text-cime-primary hover:bg-cime-primary/10 transition disabled:opacity-50"
                      title="Restaurer"
                    >
                      <RotateCcw className="w-3.5 h-3.5" />
                      Restaurer
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
